import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Auth } from 'aws-amplify';
import { AuthState, User } from '../types';
import { UserType } from '../constants/userTypes';
import { directAuth, mapAuthErrorToCode } from '../services/directAuth';
import { graphqlApi } from '../services/graphql';
import apolloClient from '../graphql/apollo-client';
import toast from 'react-hot-toast';
import i18next from 'i18next';

/**
 * Resolve a localized message for an auth failure. Prefers the Cognito error
 * code (e.g. `NotAuthorizedException`) against `auth:authErrors.<CODE>`, and
 * falls back to a provided fallback i18n key when no mapping exists.
 *
 * We deliberately do NOT echo `error.message`, since Cognito messages are
 * English-only and would bypass the active locale.
 */
function localizeAuthError(
  errorCodeOrError: string | { code?: string; name?: string; message?: string } | null | undefined,
  fallbackKey: string,
): string {
  const rawCode =
    typeof errorCodeOrError === 'string'
      ? errorCodeOrError
      : mapAuthErrorToCode(errorCodeOrError);
  // Filter out generic/unhelpful codes that would never have a translation
  // anyway. Without this, i18next's `parseMissingKeyHandler` prints the
  // literal `[MISSING: authErrors.<code>]` marker in dev mode even when
  // `defaultValue` is supplied, because the missing-key handler runs first.
  const code =
    rawCode && rawCode !== 'Error' && rawCode !== 'UNKNOWN' ? rawCode : '';
  const fallback = i18next.t(fallbackKey);
  if (!code) return fallback;
  // Guard the lookup with `exists` so we don't trip the missing-key
  // handler for codes that genuinely have no translation. `defaultValue`
  // alone is not enough in dev mode with `saveMissing: true`.
  const key = `auth:authErrors.${code}`;
  if (!i18next.exists(key)) return fallback;
  return i18next.t(key);
}

/**
 * Ensure an influencer/host user object has its profile id (`influencerId`
 * / `hostId`) populated. This id is resolved at login time, but a user who
 * completes their profile AFTER logging in (or whose login-time lookup
 * failed) ends up with a persisted session missing the id. Without it,
 * profile-gated actions (e.g. applying to a campaign) wrongly fail with
 * "프로필이 필요합니다". Re-resolve by userId and return a patched user;
 * returns the original user unchanged when nothing needs fixing.
 */
async function backfillProfileId(user: User): Promise<User> {
  try {
    if (!user?.userId) return user;
    if (user.userType === 'influencer' && !user.influencerId) {
      const res = await graphqlApi.getInfluencerByUserId(user.userId);
      if (res.success && res.data?.influencerId) {
        return { ...user, influencerId: res.data.influencerId };
      }
    }
  } catch {
    /* best-effort — never block auth restore on a profile lookup */
  }
  return user;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      setAuthenticatedUser: (user: User) => {
        set({
          user,
          isAuthenticated: true,
        });
      },

      updateUser: (user: User) => {
        set({ user });
      },

      updateUserLanguage: (language: string) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              languagePreference: language,
            },
          });
        }
      },

      login: async (email: string, password: string) => {
        try {
          console.log('🔐 Starting login process...', { email });

          // Use Amplify signIn directly to ensure proper session establishment

          const result = await Auth.signIn({
            username: email,
            password,
          });

          if (result) {
            console.log('✅ Login successful, setting user state');

            // Get current user and session from Amplify
            const [currentUser, session] = await Promise.all([
              Auth.currentAuthenticatedUser(),
              Auth.currentSession()
            ]);

            // Get user data from our backend
            console.log('🔍 Fetching user data from backend...');
            let userData = null;
            try {
              const userResponse = await directAuth.getCurrentUser();
              if (userResponse.success && userResponse.data) {
                userData = userResponse.data;
                console.log('✅ User data from directAuth:', userData);
              } else {
                console.log('⚠️ DirectAuth failed:', userResponse.error);
              }
            } catch (backendError) {
              console.log('⚠️ DirectAuth exception:', backendError);
            }

            if (userData) {
              set({
                user: userData,
                token: session.getIdToken().getJwtToken(), // Use ID token for GraphQL auth
                isAuthenticated: true,
              });
            } else {
              throw new Error('Failed to get user data from Cognito');
            }

            toast.success(i18next.t('auth:toast.loginSuccess'));
          } else {
            throw new Error('Login not completed');
          }
        } catch (error: any) {
          console.error('❌ Login error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.loginFailed'));
          throw error;
        }
      },

      register: async (
        email: string,
        password: string,
        name: string,
        userType: UserType,
        tosAcceptedVersion: string,
        tosAcceptedAt: number,
        marketingConsent?: boolean
      ) => {
        try {
          console.log('📝 Starting registration process...', { email, name, userType, tosAcceptedVersion });

          const response = await directAuth.signUp(
            email,
            password,
            name,
            userType,
            tosAcceptedVersion,
            tosAcceptedAt,
            marketingConsent
          );

          if (response.success) {
            console.log('✅ Registration successful');
            toast.success(i18next.t('auth:toast.registrationSuccess'));
            return;
          }

          // --- Explicit duplicate-email check ------------------------------
          // Cognito throws UsernameExistsException on duplicate email. We
          // handle this case specifically so the user sees a dedicated
          // "이미 가입한 이메일입니다." toast (with a route hint back to
          // login) instead of the generic registration-failed fallback.
          if (response.errorCode === 'UsernameExistsException') {
            console.warn('⚠️ Registration blocked — email already registered:', email);
            toast.error(i18next.t('auth:authErrors.UsernameExistsException'));
            const err: any = new Error('Email already registered');
            err.code = 'UsernameExistsException';
            err.duplicate = true;
            throw err;
          }

          // --- All other registration failures ----------------------------
          console.error('❌ Registration failed:', response.error);
          // Preserve the Cognito error code on the thrown Error so the
          // catch below can localize via `auth:authErrors.<code>`.
          // Throwing `new Error(response.errorCode)` alone loses the
          // code — `mapAuthErrorToCode` would fall through to
          // `error.name = 'Error'` and the UI would render
          // `[MISSING: authErrors.Error]`.
          const err: any = new Error(response.error || 'Registration failed');
          if (response.errorCode) err.code = response.errorCode;
          throw err;
        } catch (error: any) {
          // The duplicate-email branch above already toasted — don't
          // double-toast when the error propagates up here.
          if (!error?.duplicate) {
            console.error('❌ Registration error:', error);
            toast.error(localizeAuthError(error, 'auth:toast.registrationFailed'));
          }
          throw error;
        }
      },

      confirmEmail: async (email: string, confirmationCode: string) => {
        try {
          console.log('✉️ Starting email confirmation...', { email });

          const response = await directAuth.confirmEmail(email, confirmationCode);

          if (response.success) {
            console.log('✅ Email confirmation successful');
            toast.success(i18next.t('auth:toast.emailConfirmed'));
          } else {
            console.error('❌ Email confirmation failed:', response.error);
            const err: any = new Error(response.error || 'Email confirmation failed');
            if (response.errorCode) err.code = response.errorCode;
            throw err;
          }
        } catch (error: any) {
          console.error('❌ Email confirmation error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.emailConfirmationFailed'));
          throw error;
        }
      },

      resendVerificationCode: async (email: string) => {
        try {
          console.log('📧 Resending verification code...', { email });

          const response = await directAuth.resendVerificationCode(email);

          if (response.success) {
            console.log('✅ Verification code resent successfully');
            toast.success(i18next.t('auth:codeSent'));
          } else {
            console.error('❌ Failed to resend verification code:', response.error);
            const err: any = new Error(response.error || 'Failed to resend verification code');
            if (response.errorCode) err.code = response.errorCode;
            throw err;
          }
        } catch (error: any) {
          console.error('❌ Resend verification code error:', error);
          toast.error(localizeAuthError(error, 'auth:toast.emailConfirmationFailed'));
          throw error;
        }
      },

      logout: async () => {
        try {
          console.log('🚪 Starting logout process...');

          await directAuth.signOut();

          // Clear Apollo cache to prevent stale permissions on next login
          await apolloClient.clearStore();

          // Clear persisted auth storage
          localStorage.removeItem('auth-storage');

          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });

          console.log('✅ Logout successful');
          toast.success(i18next.t('auth:toast.loggedOut'));
        } catch (error: any) {
          console.error('❌ Logout error:', error);
          // Still clear local state even if Cognito signout fails
          try { await apolloClient.clearStore(); } catch { /* ignore */ }
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          toast.success(i18next.t('auth:toast.loggedOut'));
        }
      },

      checkAuthState: async () => {
        try {
          console.log('🔍 Checking authentication state...');

          const response = await directAuth.getCurrentUser();

          if (response.success && response.data) {
            console.log('✅ Found authenticated user:', response.data);

            // Defensive backfill: re-resolve influencerId when missing so a
            // stale post-login session doesn't break profile-gated actions.
            const restoredUser = await backfillProfileId(response.data);

            // Get current session for token
            try {
              const session = await Auth.currentSession();
              set({
                user: restoredUser,
                token: session.getIdToken().getJwtToken(),
                isAuthenticated: true,
              });
            } catch {
              // No Cognito session — check if we have a stored token (Naver OAuth)
              const currentState = get();
              if (currentState.token) {
                console.log('ℹ️ Using persisted token for Naver OAuth user');
                set({
                  user: restoredUser,
                  isAuthenticated: true,
                });
              } else {
                // No session and no token — not authenticated
                console.log('ℹ️ No Cognito session and no stored token');
                set({ user: null, token: null, isAuthenticated: false });
                return false;
              }
            }

            return true;
          } else {
            // No Cognito user found — try to restore session from localStorage tokens
            try {
              const session = await Auth.currentSession();
              // Session exists but getCurrentUser failed — retry
              const retryResponse = await directAuth.getCurrentUser();
              if (retryResponse.success && retryResponse.data) {
                const retryUser = await backfillProfileId(retryResponse.data);
                set({
                  user: retryUser,
                  token: session.getIdToken().getJwtToken(),
                  isAuthenticated: true,
                });
                return true;
              }
            } catch {
              // No session at all
            }

            console.log('ℹ️ No authenticated user found');
            set({ user: null, token: null, isAuthenticated: false });
            return false;
          }
        } catch (error: any) {
          console.error('❌ Auth state check failed:', error);
          set({ user: null, token: null, isAuthenticated: false });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
